import { API_ROUTES } from "definitions/constants/routeConstants";
import { AutoFormField, AutoFormSection } from "models/autoForm";

const EXTENDED_ROLE_FIELDS: AutoFormField[] = [
  {
    name: "recruiterId",
    type: "userSelect",
    label: "Recruiter",
    accessor: "recruiterId",
    writer: "newHireJourney.recruiterId",
  },
  {
    name: "itTeamContactId",
    type: "userSelect",
    label: "IT Team Contact",
    accessor: "itTeamContactId",
    writer: "newHireJourney.itTeamContactId",
  },
];

export const PENDING_FORM_SECTIONS: AutoFormSection[] = [
  {
    title: "Profile",
    emoji: "😄",
    fields: [
      {
        name: "firstName",
        type: "text",
        label: "First Name",
        accessor: "newHire.firstName",
        writer: "newHire.firstName",
        required: true,
      },
      {
        name: "lastName",
        type: "text",
        label: "Last Name",
        accessor: "newHire.lastName",
        writer: "newHire.lastName",
        required: true,
      },
      {
        name: "preferredName",
        type: "text",
        label: "Preferred Name",
        accessor: "newHire.preferredName",
        writer: "newHire.preferredName",
      },
      {
        name: "pronouns",
        type: "text",
        label: "Pronouns",
        accessor: "newHire.pronouns",
        writer: "newHire.pronouns",
      },
    ],
  },
  {
    title: "Role",
    emoji: "📋",
    fields: [
      {
        name: "startDate",
        type: "date",
        label: "Start Date",
        accessor: "startDate",
        writer: "newHireJourney.startDate",
        required: true,
      },
      {
        name: "title",
        type: "text",
        label: "Title",
        accessor: "newHire.title",
        writer: "newHire.title",
        required: true,
      },
      {
        name: "department",
        type: "createableSingleSelect",
        label: "Department",
        accessor: "newHire.department",
        writer: "newHire.department",
        optionsUrl: API_ROUTES.departments.base,
        isClearable: true,
      },
      {
        name: "employmentType",
        type: "singleSelect",
        label: "Employment Type",
        accessor: "newHire.employmentType",
        writer: "newHire.employmentType",
        options: [
          { value: "fullTime", label: "Full Time" },
          { value: "partTime", label: "Part Time" },
          { value: "contractor", label: "Contractor" },
        ],
      },
    ],
  },
  {
    title: "Contact Information",
    emoji: "📥",
    fields: [
      {
        name: "personalEmail",
        type: "email",
        label: "Personal Email",
        accessor: "newHire.personalEmail",
        writer: "newHire.personalEmail",
        required: true,
      },
      {
        name: "workEmail",
        type: "email",
        label: "Work Email",
        accessor: "newHire.workEmail",
        writer: "newHire.workEmail",
      },
      {
        name: "linkedinUrl",
        type: "text",
        label: "LinkedIn Url",
        accessor: "newHire.linkedinUrl",
        writer: "newHire.linkedinUrl",
      },
      {
        name: "phoneNumber",
        type: "text",
        label: "Phone",
        accessor: "newHire.phoneNumber",
        writer: "newHire.phoneNumber",
      },
    ],
  },
  {
    title: "Location",
    emoji: "📍",
    fields: [
      {
        name: "workMode",
        type: "singleSelect",
        label: "Work Mode",
        accessor: "newHire.workMode",
        writer: "newHire.workMode",
        options: [
          { value: "remote", label: "Remote" },
          { value: "onsite", label: "Onsite" },
          { value: "hybrid", label: "Hybrid" },
        ],
      },
      {
        name: "location",
        type: "locationSelect",
        label: "Location",
        accessor: "newHire.location",
        writer: "newHire.location",
        required: true,
        hiddenFields: [
          { name: "country", accessor: "newHire.country", writer: "newHire.country" },
          { name: "googleLocationId", accessor: "newHire.googleLocationId", writer: "newHire.googleLocationId" },
          { name: "rawLocation", accessor: "newHire.rawLocation", writer: "newHire.rawLocation" },
        ],
        associatedFields: [{ name: "timezone", accessor: "newHire.timezone", writer: "newHire.timezone" }],
      },
      {
        name: "timezone",
        type: "timezoneSelect",
        label: "Timezone",
        accessor: "newHire.timezone",
        writer: "newHire.timezone",
        required: true,
      },
    ],
  },
  {
    title: "Support Team",
    emoji: "🌱",
    fields: [
      {
        name: "managerId",
        type: "userSelect",
        label: "Manager",
        accessor: "newHire.managerId",
        writer: "newHire.managerId",
        required: true,
      },
      {
        name: "peopleTeamContactId",
        type: "userSelect",
        label: "People Team Contact",
        accessor: "peopleTeamContactId",
        writer: "newHireJourney.peopleTeamContactId",
        required: true,
      },
      {
        name: "buddyId",
        type: "userSelect",
        label: "Buddy",
        accessor: "buddyId",
        writer: "newHireJourney.buddyId",
        helperText: "Typically assigned by manager. Can add now if known",
      },
    ],
  },
];

const EXTENDED_ROLE_FIELD_NAMES = EXTENDED_ROLE_FIELDS.map((field) => field.name);

interface PendingFormSectionOptions {
  extendedRolesEnabled?: boolean;
}

export function getPendingFormSections({
  extendedRolesEnabled = false,
}: PendingFormSectionOptions = {}): AutoFormSection[] {
  if (!extendedRolesEnabled) return PENDING_FORM_SECTIONS;

  return PENDING_FORM_SECTIONS.map((section) => {
    if (section.title !== "Support Team") return section;
    return { ...section, fields: [...section.fields, ...EXTENDED_ROLE_FIELDS] };
  });
}

/**
 * Companies with a custom Form record have the extended role fields persisted in
 * their schema (added by AddExtendedRoleFieldsToPendingNewHireForms). Strip them
 * out at render time when the feature flag is off so visibility stays gated by
 * the flag, not by data presence.
 */
export function applyExtendedRoleVisibility(
  sections: AutoFormSection[],
  extendedRolesEnabled: boolean,
): AutoFormSection[] {
  if (extendedRolesEnabled) return sections;

  return sections.map((section) => ({
    ...section,
    fields: section.fields.filter((field) => !EXTENDED_ROLE_FIELD_NAMES.includes(field.name)),
  }));
}
